import React, { FC, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { replace as Replace } from 'redux-first-history';
import {
    Campaign,
    CampaignTemplate,
    CampaignType,
    DeliveryLogType,
    StatusMessageCategory,
} from '@dev/ee-api/ee-api-models';
import { Paper } from '@dev/ee-components';
import { Box } from '@material-ui/core';
import { isEmpty } from 'lodash-es';
import { parse } from 'query-string';
import { useLocation } from 'react-router-dom';
import { safelyDecodeBase64Url } from '@dev/ee-tools';

import ContactEventHistoryOverlay from '@/components/Activity/ContactEventHistory/Overlay';
import { ApplicationState } from '@/store';
import { EventLogByChannelNameStart } from '@/store/reports/actions';
import { EventLogParamsByChannelName } from '@/store/reports/types';
import { AbSplitDisplayMode } from '@/store/campaigns/types';
import { GetCampaignByName, SetPickedContactEmail } from '@/store/campaigns/actions';
import { useMobile } from '@/services/hooks/useMobile';
import { addParamToUrl } from '@/services/tools/handleUrlParams';

import { debounce } from '@/services/tools/debounce';
import { CampaignReportActivityProps } from './CampaignReportActivityInterface';
import { getIsWinner } from '../../helperFunctions';
import PaginationComponent from './components/PaginationComponent';
import FiltersComponent from './components/FiltersComponent';
import BannerComponent from './components/BannerComponent';
import TableComponent from './components/TableComponent';
import {
    convertMessageCategoryAndStatuses,
    splitSelectedItemsIntoProperState,
} from '../../assets/EventFilter/helperFunctions';
import { useRequestParams } from './hooks/useRequestParams';

const getChannelName = (
    isAbSplit: boolean,
    urlChannelName: string,
    campaign?: Campaign,
    currentTemplate?: string,
    displayMode?: AbSplitDisplayMode
) => {
    if (isAbSplit && campaign) {
        if (displayMode === AbSplitDisplayMode.TestResult) {
            return currentTemplate;
        } else {
            const winnerChannel = getIsWinner(campaign.CampaignTemplates) as CampaignTemplate;
            return winnerChannel.Name;
        }
    }

    return safelyDecodeBase64Url(urlChannelName);
};

const CampaignReportActivity: FC<CampaignReportActivityProps> = (props: CampaignReportActivityProps): JSX.Element => {
    const dispatch = useDispatch();
    const isMobile = useMobile();

    const {
        reports: { eventLogParamsByChannelName },
        emails,
        campaigns: {
            pickedCampaign: { campaign },
            abSplitHeaderControls: { displayMode, currentTemplate },
        },
    } = useSelector((state: ApplicationState) => state);

    const {
        match: { params },
    } = props;

    const onStatusFilterClick = (selectedItems: (DeliveryLogType | StatusMessageCategory)[]): void => {
        debounce(() => {
            const [events, selectedResult] = splitSelectedItemsIntoProperState(selectedItems);
            const [convertedStatuses, convertedResults] = convertMessageCategoryAndStatuses(events, selectedResult);

            const params: EventLogParamsByChannelName = {
                statuses: isEmpty(convertedStatuses) ? [DeliveryLogType.All] : convertedStatuses,
                offset: 0,
                limit: eventLogParamsByChannelName?.limit,
                channelName: eventLogParamsByChannelName?.channelName,
                messageCategory: isEmpty(selectedResult) ? null : convertedResults,
                email: eventLogParamsByChannelName?.email,
                from: eventLogParamsByChannelName?.from,
                to: eventLogParamsByChannelName?.to,
                lastEvent: eventLogParamsByChannelName?.lastEvent,
            };
            const newSearch = addParamToUrl({
                events: !isEmpty(convertedStatuses) && convertedStatuses,
                result: !isEmpty(selectedResult) && convertedResults,
                page: 1,
            });

            dispatch(Replace({ search: newSearch }));
            dispatch(EventLogByChannelNameStart(params));
        }, 1000);
    };

    const isAbSplit = params.type === CampaignType.AbSplit;
    const location = useLocation();
    const query = parse(location.search, { parseBooleans: true });
    const decodedName = getChannelName(isAbSplit, params.name, campaign, currentTemplate, displayMode);
    const isLastEvent = (query?.lastEvent as boolean) ?? true;
    const requestParams = useRequestParams(decodedName, isLastEvent);

    useEffect(() => {
        if (isAbSplit && !campaign) {
            dispatch(
                GetCampaignByName({
                    name: decodedName,
                    campaignType: params.type,
                })
            );
        } else {
            dispatch(EventLogByChannelNameStart(requestParams));
        }
        return () => {
            dispatch(SetPickedContactEmail(null));
        };
    }, []);

    useEffect(() => {
        if (displayMode && currentTemplate) {
            dispatch(EventLogByChannelNameStart(requestParams));
        }
    }, [displayMode, currentTemplate]);
    
    if (isMobile) {
        return (
            <Box pt="32px">
                <BannerComponent channelName={decodedName} />
                <FiltersComponent lastEventOnly={isLastEvent} search={requestParams.email} channelName={decodedName} />
                <TableComponent />
                <PaginationComponent channelName={decodedName} />
                {emails.msgId && <ContactEventHistoryOverlay />}
            </Box>
        );
    }

    return (
        <Box pt="32px">
            <BannerComponent channelName={decodedName} />
            <Paper padding={{ top: 24 }}>
                <FiltersComponent lastEventOnly={isLastEvent} search={requestParams.email} channelName={decodedName} />
                <TableComponent onStatusFilterClick={onStatusFilterClick} />
                <PaginationComponent channelName={decodedName} />
            </Paper>
            {emails.msgId && <ContactEventHistoryOverlay />}
        </Box>

    );
};

export default CampaignReportActivity;
